import React, { useState, useEffect } from 'react';
import { Row, Col, Checkbox, Button, Typography, Spin, Tooltip } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import BacklogItem from './BacklogItem';
import CreateTaskBar from './CreateTaskBar';
import { BulletList } from 'react-content-loader';
import { toast } from 'react-toastify';
import httpInjectorService from 'services/http-injector.service';
import dayjs from 'dayjs';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Cookies from 'js-cookie';
import { useFetchBacklogData } from 'views/admin/taskManagement/BackLogPage/Alldata';

const { Title } = Typography;

// Sortable BacklogItem wrapper
function SortableBacklogItem(props) {
  const { task } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    marginBottom: 8,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <BacklogItem {...props} />
    </div>
  );
}

const Backlog = ({
  collapsed,
  setCollapsed,
  handleCreateSprint,
  createdTasks,
  projectId,
  fetchBacklogData,
  handleDeleteTask,
  //sprintBacklogList,
  sprintList,
  handleMoveTask,
  sprints,
  epicOptions,
  statusOptions,
  assigneeOptions,
  labelOptions,
  filterdtasks,
  handleUpdateLabel,
  backlogData,
  handleTaskClick,
  backlogCounts,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFields, setShowFields] = useState(false);
  // const [loading, setLoading] = useState(false);
  const roleId = Number(Cookies.get('task-role-id'));

  // const { fetchBacklogData } = useFetchBacklogData();

  const tasksToRender =
    Array.isArray(filterdtasks) && filterdtasks.length > 0
      ? filterdtasks
      : createdTasks || [];


  const tasks = tasksToRender || [];

  // Local state for drag-and-drop ordering
  // const [tasks, setTasks] = useState(tasksToRender);

  // // Keep tasks in sync with createdTasks prop
  // React.useEffect(() => {
  //   setTasks(tasksToRender || []);
  // }, [tasksToRender]);

  const getStatusStyle = (status) => {
    const s = status?.toLowerCase();

    if (s === 'done' || s === 'completed') {
      return { background: '#e6f4ea', color: '#1e8e3e' };
    }

    if (s === 'to do' || s === 'todo') {
      return { background: '#f0f0f0', color: '#595959' };
    }

    return { background: '#e6f4ff', color: '#1677ff' };
  };

  const statusBadges = Object.entries(backlogCounts?.byStatus || {});

  // dnd-kit sensors
  // const sensors = useSensors(
  //   useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  // );

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;
  //   if (active.id !== over?.id) {
  //     const oldIndex = tasks.findIndex((t) => t.id === active.id);
  //     const newIndex = tasks.findIndex((t) => t.id === over.id);
  //     const newTasks = arrayMove(tasks, oldIndex, newIndex);
  //     setTasks(newTasks);
  //     // Optionally, call an API or callback to persist new order here
  //   }
  // };

  const handleSelectOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id),
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(
      checked && Array.isArray(tasks) ? tasks.map((t) => t.key) : [],
    );
  };

  const handleUpdateTaskFromBacklog = async (task) => {
    const existingTask = tasks.find((t) => t.id === (task.issue_id || task.id));

    const payload = {
      project_id: projectId,
      issue_id: task.issue_id || task.id,
      issue_type: task.issue_type,
      issue_type_id: task.issue_type_id ?? existingTask?.issue_type_id,
      issue_title: task.title || existingTask?.title || '',
      issue_status: task.status || existingTask?.status || '',
      assignee_id:
        task.assignee_id ??
        task.assignee?.id ??
        existingTask?.assignee_id ??
        existingTask?.assignee?.id ??
        null,
      epic_id: task.epic?.epic_id || task.epic_id || existingTask?.epic_id,
      story_points: task.story_points || existingTask?.story_points,
      due_date: task.dueDate
        ? dayjs(task.dueDate, ['DD MMM', 'YYYY-MM-DD']).format('YYYY-MM-DD')
        : existingTask?.dueDate
          ? dayjs(existingTask.dueDate, ['DD MMM', 'YYYY-MM-DD']).format(
              'YYYY-MM-DD',
            )
          : null,
      description: task.description ?? existingTask?.description ?? '',
       work_item_url:`${window.location.origin}/admin/taskBoard/${projectId}/taskDetails/${task.issue_id || task.id}`
    };
    try {
      const response = await httpInjectorService.updateTaskFromBacklog(payload);

      if (response.status === 'success') {
        await fetchBacklogData?.();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddTask = async (task) => {
    if (!task?.issue_title || !task?.issue_status) {
      toast.error('Task title and status are required.');
      return;
    }

    const payload = {
      project_id: projectId,
      issue_title: task.issue_title,
      issue_status: task.issue_status || 'TO DO',
      issue_type: task.issue_type,
      issue_type_id: task.issue_type_id,
    };

    try {
      const response = await httpInjectorService.createTaskToBacklog(payload);

      if (response.status === 'success') {
        toast.success(response.message);
        await fetchBacklogData?.();
      } else {
        toast.error(response.message || 'Failed to add task to backlog.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while adding the task.');
    }
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {/* <Checkbox
            checked={
              selectedIds.length > 0 && selectedIds.length === tasks.length
            }
            indeterminate={
              selectedIds.length > 0 && selectedIds.length !== tasks.length
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          /> */}
          <Button
            type="link"
            icon={collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ marginLeft: 8, color: 'black' }}
          >
            <Title level={5} style={{ display: 'inline-block', margin: 0 }}>
              Backlog ({Array.isArray(tasks) ? tasks.length : 0} work items)
            </Title>
          </Button>
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }} >
          {statusBadges.map(([status, count]) => (
            <Tooltip key={status} title={status}>
              <span
                style={{
                  minWidth: 24,
                  height: 24,
                  padding: '0 6px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'default',
                  ...getStatusStyle(status),
                }}
              >
                {count}
              </span>
            </Tooltip>
          ))}

          {[1, 2].includes(roleId) && (
            <Button
              style={{ backgroundColor: '#722ed1', color: 'white' }}
              onClick={() => {
                const selectedTasks = (tasks || []).filter((task) =>
                  selectedIds.includes(task.key),
                );
                handleCreateSprint(selectedTasks);
              }}
              data-tour="create-sprint"
            >
              Create Sprint
            </Button>
          )}
        </Col>
      </Row>

      {!collapsed && (
        <>
          {(!Array.isArray(tasks) || tasks.length === 0) && (
            <div style={{ padding: 16, color: '#999' }}>
              No tasks found in the backlog.
            </div>
          )}

          {Array.isArray(tasks) && tasks.length > 0 && (
            // <SortableContext
            //   items={tasks.map((task) => task.key)}
            //   strategy={verticalListSortingStrategy}
            // >
            <div style={{ minHeight: 40 }}>
              {tasks.map((task) => (
                <SortableBacklogItem
                  key={task.key}
                  id={task.id}
                  task={task}
                  epicOptions={epicOptions}
                  statusOptions={statusOptions}
                  assigneeOptions={assigneeOptions}
                  labelOptions={labelOptions}
                  isSelected={selectedIds.includes(task.key)}
                  onSelect={handleSelectOne}
                  onUpdateTask={handleUpdateTaskFromBacklog}
                  handleDeleteTask={handleDeleteTask}
                  sprintList={sprintList}
                  handleMoveTask={handleMoveTask}
                  sprints={sprints}
                  handleUpdateLabel={handleUpdateLabel}
                  handleTaskClick={handleTaskClick}
                />
              ))}
            </div>
            // </SortableContext>
          )}

          <div style={{ marginTop: 16 }}>
            {[1, 2, 3].includes(roleId) &&
              (showFields ? (
                <CreateTaskBar
                  onAddTask={handleAddTask}
                  onClose={() => setShowFields(false)}
                />
              ) : (
                <Button
                  style={{ backgroundColor: '#722ed1', color: 'white' }}
                  onClick={() => setShowFields(true)}
                >
                  + Create
                </Button>
              ))}
          </div>
        </>
      )}
    </>
  );
};

export default Backlog;
